import { invoke } from '@tauri-apps/api/core';
import { accountEmail } from './vaultAccountEmail';
import { vaultErrorToken } from '../store/vaultStore';
import { useAuthStore } from '../store/authStore';

/**
 * Changing the master password = a new vault key (Standalone vault card 2.4).
 *
 * Two steps, like first-time setup: `prepare` derives everything and returns
 * the NEW 12 words without writing anything; `commit` (the words screen's
 * Done) rewrites the vault under the new key. Until then the old passphrase
 * and the old words keep working — closing the app in between loses nothing.
 */
export async function prepareRekey({ current, next, confirm }) {
  return invoke('vault_master_rekey_prepare', {
    current,
    next,
    confirm,
    accountEmail: accountEmail(),
  });
}

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Commits a prepared re-key. A synced vault needs a sign-in from the last few
 * minutes with a second factor (the server refuses the new key otherwise);
 * when that is missing the browser sign-in opens once and the commit is
 * retried with the fresh session. Signing in as a DIFFERENT account aborts —
 * the vault belongs to the account it was prepared under.
 */
export async function commitRekey(handle, { reauth = useAuthStore.getState().loginWithBrowser } = {}) {
  let signedInAgain = false;
  for (let attempt = 0; attempt < 4; attempt += 1) {
    try {
      return await invoke('vault_master_rekey_commit', { handle });
    } catch (e) {
      if (vaultErrorToken(e) !== 'rekey_reauth_required') throw e;
      if (signedInAgain) {
        // The engine picks the new token up a moment after the sign-in.
        await wait(1500);
        continue;
      }
      const before = useAuthStore.getState().user?.id;
      const result = await reauth();
      if (!result?.success) throw new Error(result?.cancelled ? 'rekey_reauth_cancelled' : 'rekey_reauth_required');
      if (before && useAuthStore.getState().user?.id !== before) throw new Error('rekey_account_changed');
      signedInAgain = true;
    }
  }
  throw new Error('rekey_reauth_required');
}

/** Card 2.5: the user types the master password that was set on another device. */
export async function adoptRekey(secret) {
  return invoke('vault_adopt_rekey', { secret });
}

/** Copy for the re-key's own error tokens; `null` for anything else. */
export function rekeyErrorText(t, e) {
  switch (vaultErrorToken(e)) {
    case 'rekey_reauth_required':
    case 'rekey_reauth_cancelled':
      return t(
        'vault.rekeyReauth',
        'Sign in again to confirm it is you. Your vault is synced, so changing its passphrase needs a sign-in from the last few minutes. Nothing was changed.'
      );
    case 'rekey_second_factor_required':
      return t(
        'vault.rekeySecondFactor',
        'Turn on two-step sign-in (an authenticator app or a passkey) for your account first. A synced vault only takes a new passphrase from a sign-in that used it. Nothing was changed.'
      );
    case 'rekey_sync_pending':
      return t(
        'vault.rekeySyncPending',
        'Your vault is still syncing. Wait until it is up to date, then try again. Nothing was changed.'
      );
    case 'rekey_account_changed':
      return t(
        'vault.rekeyAccountChanged',
        'You signed in with a different account, so the passphrase was not changed.'
      );
    case 'vault_rekey_unverified':
      return t(
        'vault.rekeyUnverified',
        'That passphrase opens a vault key this computer cannot verify, so it was not used. Nothing was changed.'
      );
    default: {
      // `vault_rekey_item_unreadable:<id>` carries the id, so it is not a bare token.
      const raw = typeof e === 'string' ? e : String(e?.message || e || '');
      if (raw.startsWith('vault_rekey_item_unreadable')) {
        return t(
          'vault.rekeyItemUnreadable',
          'One item in this vault cannot be read, so the passphrase was not changed. Nothing was changed or lost.'
        );
      }
      return null;
    }
  }
}
